import TreeViewComponent from "@CubeComponents/html/TreeView";
import { Suspense, lazy, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FormflowsWrapper from "../../../../projects/pages/ProjectSettings/formFlowWrapper";
import type { SlickDataView, SlickgridReactInstance, OnClickEventArgs } from "slickgrid-react";

/** One row of the settings tree: a parent group (`parentId: null`) or a selectable leaf. */
type SettingsTreeRow = { id: string; name: string; parentId: string | null };

/**
 * `TreeViewComponent` forwards this ref straight through to the underlying SlickGrid instance, so the
 * handle is SlickGrid's own, narrowed to the row type this tree renders.
 */
type SettingsTreeRef = Omit<SlickgridReactInstance, "dataView"> & {
  dataView: SlickDataView<SettingsTreeRow>;
};

/**
 * What the row click actually receives. NOTE: `SlickGrid`'s own `onClick` prop is declared
 * `(e: SlickEventArgs, args: SlickEventArgs)`, but it forwards the DOM CustomEvent through
 * `as unknown as` — so the declaration is wrong and this is the real shape.
 */
type SettingsTreeClickEvent = CustomEvent<{ eventData: unknown; args: OnClickEventArgs }>;

const ReviewFlows = lazy(() => import("@Features/projects/pages/ProjectSettings/ReviewFlowsWrapper"));

function Settings({ projectId }: { projectId: string }) {
  const treeRef = useRef<SettingsTreeRef | null>(null);

  const [urlSearchParams] = useSearchParams();
  const tab = urlSearchParams.get("tab");
  const [selected, setSelected] = useState(tab || "formFlows");

  const dataSource = useMemo(
    (): SettingsTreeRow[] => [
      { id: "userAdministration", name: "User Administration", parentId: null },
      { id: "reviewFlows", name: "Review Flows", parentId: "userAdministration" },
      { id: "formFlows", name: "Form Flows", parentId: "userAdministration" },
    ],
    [],
  );

  // Highlight the default selected row once the grid is ready
  const onReactGridCreated = () => {
    const dv = treeRef.current?.dataView;
    const grid = treeRef.current?.slickGrid;
    if (!dv || !grid) return;
    const idx = dv.getIdxById(selected);
    if (idx != null && idx >= 0) {
      grid.setSelectedRows([idx]);
    }
  };

  const handleClick = (e: SettingsTreeClickEvent) => {
    const row = e.detail?.args?.row;
    if (row == null) return;
    const dv = treeRef.current?.dataView;
    const grid = treeRef.current?.slickGrid;
    if (!dv || !grid) return;
    const item = dv.getItem(row);
    if (!item || item.parentId === null) return; // skip parent nodes
    grid.setSelectedRows([row]);
    setSelected(item.id);
  };

  return (
    <div className="flex border-t border-system_colour_44 h-full w-full overflow-auto">
      <div className="flex-none w-56">
        <TreeViewComponent
          ref={treeRef}
          treeData={dataSource}
          columnId="name"
          identifierPropName="id"
          parentPropName="parentId"
          disableHeader
          height="100%"
          onClick={handleClick}
          onReactGridCreated={onReactGridCreated}
        />
      </div>
      <div className="overflow-auto w-full">
        {selected == "reviewFlows" && (
          <Suspense>
            <ReviewFlows projectId={projectId} />
          </Suspense>
        )}
        {selected == "formFlows" && (
          <Suspense>
            <FormflowsWrapper projectId={projectId} />
          </Suspense>
        )}
      </div>
    </div>
  );
}

export default Settings;
